import * as actionTypes from './actionTypes';
import { CompanyActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Company, CompaniesList } from '../../domain/Company';

export type CompanyStateType = {
  companiesList: CompaniesList | null;
  companiesLoading: boolean;
  companiesError: HttpError;
  companyOptions: Company[];
  companyOptionsLoading: boolean;
  companyOptionsError: HttpError;
  companiesListUpdateNeeded: boolean;
  companyCreateLoading: boolean;
  companyCreateError: HttpError;
  companyCreateSuccess: boolean;
  createdCompany: Company | null;
  companyUpdateLoading: boolean;
  companyUpdateError: HttpError;
  companyUpdateSuccess: boolean;
  company: Company | null;
  companyLoading: boolean;
  companyError: HttpError;
  selectedCompany: number | null;
  toggleCompanyStatusLoading: boolean;
  toggleCompanyStatusSuccess: boolean;
  toggleCompanyStatusError: HttpError;
};

export type CompanyActionType = CompanyStateType & {
  type: CompanyActionTypes;
};

export const initialState: CompanyStateType = {
  companiesList: null,
  companiesLoading: true,
  companiesError: null,
  companyOptions: [],
  companyOptionsLoading: true,
  companyOptionsError: null,
  companiesListUpdateNeeded: false,
  companyCreateLoading: false,
  companyCreateError: null,
  companyCreateSuccess: false,
  createdCompany: null,
  companyUpdateLoading: false,
  companyUpdateError: null,
  companyUpdateSuccess: false,
  company: null,
  companyLoading: false,
  companyError: null,
  toggleCompanyStatusError: null,
  toggleCompanyStatusSuccess: false,
  toggleCompanyStatusLoading: false,
  selectedCompany: localStorage.getItem('selectedCompany')
    ? Number(localStorage.getItem('selectedCompany'))
    : null,
};

const fetchCompaniesStart = (state: CompanyStateType): CompanyStateType => ({
  ...state,
  companiesLoading: true,
});

const fetchCompaniesSuccess = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companiesList: action.companiesList,
  companiesLoading: false,
  companiesError: null,
  companiesListUpdateNeeded: false,
});

const fetchCompaniesFail = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companiesError: action.companiesError,
  companiesLoading: false,
});

const fetchCompanyOptionsStart = (
  state: CompanyStateType,
): CompanyStateType => ({
  ...state,
  companyOptionsLoading: true,
});

const fetchCompanyOptionsSuccess = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companyOptions: action.companyOptions,
  companyOptionsLoading: false,
  companyOptionsError: null,
});

const fetchCompanyOptionsFail = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companyOptionsError: action.companyOptionsError,
  companyOptionsLoading: false,
});

const fetchCompanyStart = (state: CompanyStateType): CompanyStateType => ({
  ...state,
  companyLoading: true,
  companyUpdateSuccess: false,
  companyCreateSuccess: false,
  companyCreateError: null,
  companyUpdateError: null,
});

const fetchCompanySuccess = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  company: action.company,
  companyLoading: false,
  companyError: null,
});

const fetchCompanyFail = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companyError: action.companyError,
  companyLoading: false,
});

const createCompanyStart = (state: CompanyStateType): CompanyStateType => ({
  ...state,
  companyCreateLoading: true,
});

const createCompanySuccess = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companyCreateLoading: false,
  companyCreateError: null,
  companyCreateSuccess: true,
  createdCompany: action.createdCompany,
  companiesListUpdateNeeded: true,
});

const createCompanyFail = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companyCreateLoading: false,
  companyCreateError: action.companyCreateError,
});

const updateCompanyStart = (state: CompanyStateType): CompanyStateType => ({
  ...state,
  companyUpdateLoading: true,
});

const updateCompanySuccess = (state: CompanyStateType): CompanyStateType => ({
  ...state,
  companyUpdateLoading: false,
  companyUpdateError: null,
  companyUpdateSuccess: true,
  companiesListUpdateNeeded: true,
});

const updateCompanyFail = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  companyUpdateLoading: false,
  companyUpdateError: action.companyUpdateError,
});

const setSelectedCompany = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  selectedCompany: action.selectedCompany,
});

const toggleCompanyStatusStart = (
  state: CompanyStateType,
): CompanyStateType => ({
  ...state,
  toggleCompanyStatusLoading: true,
});

const toggleCompanyStatusSuccess = (
  state: CompanyStateType,
): CompanyStateType => ({
  ...state,
  toggleCompanyStatusLoading: false,
  toggleCompanyStatusError: null,
  toggleCompanyStatusSuccess: true,
  companiesListUpdateNeeded: true,
});

const toggleCompanyStatusFail = (
  state: CompanyStateType,
  action: CompanyActionType,
): CompanyStateType => ({
  ...state,
  toggleCompanyStatusLoading: false,
  toggleCompanyStatusError: action.toggleCompanyStatusError,
});

const resetCompanyStore = (): CompanyStateType => ({
  companiesList: null,
  companiesLoading: true,
  companiesError: null,
  companyOptions: [],
  companyOptionsLoading: true,
  companyOptionsError: null,
  companiesListUpdateNeeded: false,
  companyCreateLoading: false,
  companyCreateError: null,
  companyCreateSuccess: false,
  createdCompany: null,
  companyUpdateLoading: false,
  companyUpdateError: null,
  companyUpdateSuccess: false,
  company: null,
  companyLoading: false,
  companyError: null,
  toggleCompanyStatusError: null,
  toggleCompanyStatusLoading: false,
  toggleCompanyStatusSuccess: false,
  selectedCompany: localStorage.getItem('selectedCompany')
    ? Number(localStorage.getItem('selectedCompany'))
    : null,
});

const reducer = (state = initialState, action: CompanyActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_COMPANIES_START:
      return fetchCompaniesStart(state);
    case actionTypes.FETCH_COMPANIES_SUCCESS:
      return fetchCompaniesSuccess(state, action);
    case actionTypes.FETCH_COMPANIES_FAIL:
      return fetchCompaniesFail(state, action);
    case actionTypes.FETCH_COMPANY_OPTIONS_START:
      return fetchCompanyOptionsStart(state);
    case actionTypes.FETCH_COMPANY_OPTIONS_SUCCESS:
      return fetchCompanyOptionsSuccess(state, action);
    case actionTypes.FETCH_COMPANY_OPTIONS_FAIL:
      return fetchCompanyOptionsFail(state, action);
    case actionTypes.FETCH_COMPANY_START:
      return fetchCompanyStart(state);
    case actionTypes.FETCH_COMPANY_SUCCESS:
      return fetchCompanySuccess(state, action);
    case actionTypes.FETCH_COMPANY_FAIL:
      return fetchCompanyFail(state, action);
    case actionTypes.CREATE_COMPANY_START:
      return createCompanyStart(state);
    case actionTypes.CREATE_COMPANY_SUCCESS:
      return createCompanySuccess(state, action);
    case actionTypes.CREATE_COMPANY_FAIL:
      return createCompanyFail(state, action);
    case actionTypes.UPDATE_COMPANY_START:
      return updateCompanyStart(state);
    case actionTypes.UPDATE_COMPANY_SUCCESS:
      return updateCompanySuccess(state);
    case actionTypes.UPDATE_COMPANY_FAIL:
      return updateCompanyFail(state, action);
    case actionTypes.TOGGLE_COMPANY_STATUS_START:
      return toggleCompanyStatusStart(state);
    case actionTypes.TOGGLE_COMPANY_STATUS_SUCCESS:
      return toggleCompanyStatusSuccess(state);
    case actionTypes.TOGGLE_COMPANY_STATUS_FAIL:
      return toggleCompanyStatusFail(state, action);
    case actionTypes.SET_SELECTED_COMPANY:
      return setSelectedCompany(state, action);
    case actionTypes.RESET_COMPANY_STORE:
      return resetCompanyStore();
    default:
      return state;
  }
};

export default reducer;
